import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { ColorBlock, SectionHeading, BodyText, Glass, SectionImage, BLOCKS, P, PD, A } from './BlogShared.jsx';
import insightCards from '../../assets/blog/insight-cards.png';

/* ─── Glassmorphic 3D icons for capabilities ─── */
const IconPOS = ({ size = 44 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="iconPOSGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={PD} />
        <stop offset="100%" stopColor={P} />
      </linearGradient>
      <linearGradient id="iconGlass" x1="0" y1="0" x2="0.5" y2="1">
        <stop offset="0%" stopColor="white" stopOpacity="0.7" />
        <stop offset="100%" stopColor="white" stopOpacity="0.2" />
      </linearGradient>
    </defs>
    <rect x="6" y="7" width="36" height="26" rx="6" fill="url(#iconGlass)" stroke="url(#iconPOSGrad)" strokeWidth="2" />
    <rect x="11" y="12" width="26" height="16" rx="3" fill={`${P}12`} />
    <rect x="13" y="14" width="10" height="4" rx="1.5" fill="rgba(255,255,255,0.6)" />
    <circle cx="35" cy="17" r="2" fill={P} opacity="0.4" />
    <path d="M17 37h14" stroke="url(#iconPOSGrad)" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M24 33v4" stroke="url(#iconPOSGrad)" strokeWidth="2.5" />
  </svg>
);
const IconKitchen = ({ size = 44 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="iconKitGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={A} />
        <stop offset="100%" stopColor="#FF1744" />
      </linearGradient>
    </defs>
    <rect x="8" y="16" width="32" height="22" rx="6" fill="url(#iconGlass)" stroke="url(#iconKitGrad)" strokeWidth="2" />
    <path d="M14 16V12a10 10 0 0120 0v4" stroke="url(#iconKitGrad)" strokeWidth="2.5" />
    <circle cx="24" cy="27" r="6" fill={`${A}15`} stroke="url(#iconKitGrad)" strokeWidth="1.5" />
    <circle cx="24" cy="27" r="3" fill="url(#iconKitGrad)" opacity="0.4" />
    <ellipse cx="22" cy="25" rx="1.5" ry="1" fill="rgba(255,255,255,0.5)" />
  </svg>
);
const IconInventory = ({ size = 44 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="iconInvGrad" x1="0.2" y1="0" x2="0.8" y2="1">
        <stop offset="0%" stopColor={PD} />
        <stop offset="100%" stopColor={P} />
      </linearGradient>
    </defs>
    <rect x="6" y="8" width="36" height="32" rx="6" fill="url(#iconGlass)" stroke="url(#iconInvGrad)" strokeWidth="2" />
    <path d="M6 18h36M6 28h36" stroke={`${P}20`} strokeWidth="1" />
    <rect x="11" y="10.5" width="10" height="5" rx="2.5" fill="url(#iconInvGrad)" opacity="0.6" />
    <rect x="11" y="20.5" width="8" height="5" rx="2.5" fill="url(#iconInvGrad)" opacity="0.4" />
    <rect x="11" y="30.5" width="14" height="5" rx="2.5" fill="url(#iconInvGrad)" opacity="0.5" />
    <circle cx="35" cy="13" r="3" fill={P} opacity="0.2" />
    <ellipse cx="34" cy="12" rx="1.2" ry="0.8" fill="rgba(255,255,255,0.4)" />
  </svg>
);
const IconInsights = ({ size = 44 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="iconInsGrad" x1="0" y1="1" x2="1" y2="0">
        <stop offset="0%" stopColor={A} />
        <stop offset="100%" stopColor={P} />
      </linearGradient>
    </defs>
    <path d="M8 38V24l8-5 8 8 8-14 8-3v28H8z" fill={`${A}08`} />
    <path d="M8 24l8-5 8 8 8-14 8-3" stroke="url(#iconInsGrad)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="40" cy="10" r="5" fill={`${A}15`} />
    <circle cx="40" cy="10" r="3" fill="url(#iconInsGrad)" />
    <ellipse cx="39" cy="8.5" rx="1.5" ry="1" fill="rgba(255,255,255,0.5)" />
    <path d="M8 38h34" stroke="url(#iconInsGrad)" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
  </svg>
);
const IconAccess = ({ size = 44 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="iconAccGrad" x1="0.3" y1="0" x2="0.7" y2="1">
        <stop offset="0%" stopColor={PD} />
        <stop offset="100%" stopColor={A} />
      </linearGradient>
    </defs>
    <circle cx="24" cy="16" r="8" fill={`${P}10`} stroke="url(#iconAccGrad)" strokeWidth="2" />
    <circle cx="24" cy="16" r="4" fill="url(#iconAccGrad)" opacity="0.3" />
    <ellipse cx="22.5" cy="14" rx="2" ry="1.5" fill="rgba(255,255,255,0.4)" />
    <path d="M12 40c0-7 5-12 12-12s12 5 12 12" stroke="url(#iconAccGrad)" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);
const IconAI = ({ size = 44 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} fill="none">
    <defs>
      <linearGradient id="iconAIGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={A} />
        <stop offset="100%" stopColor={P} />
      </linearGradient>
    </defs>
    <rect x="8" y="10" width="32" height="28" rx="8" fill="url(#iconGlass)" stroke="url(#iconAIGrad)" strokeWidth="2" />
    <circle cx="18" cy="24" r="3" fill="url(#iconAIGrad)" opacity="0.5" />
    <circle cx="30" cy="24" r="3" fill="url(#iconAIGrad)" opacity="0.5" />
    <path d="M18 31c2 2 10 2 12 0" stroke="url(#iconAIGrad)" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M24 10V6" stroke="url(#iconAIGrad)" strokeWidth="2" strokeLinecap="round" />
    <circle cx="24" cy="5" r="2" fill="url(#iconAIGrad)" opacity="0.4" />
    <ellipse cx="22" cy="4" rx="1" ry="0.6" fill="rgba(255,255,255,0.4)" />
  </svg>
);

/* ─── Flow diagram (Collect → Analyze → Predict → Act) ─── */
const FlowDiagram = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) el.classList.add('shift-active'); },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const steps = [
    { label: 'Collect', sub: 'Orders, inventory, patterns', color: P },
    { label: 'Analyze', sub: 'AI processes your data', color: PD },
    { label: 'Predict', sub: 'What happens next', color: A },
    { label: 'Act', sub: 'Clear recommendations', color: P },
  ];

  return (
    <Box ref={ref} sx={{ maxWidth: 700, mx: 'auto' }}>
      <style>{`
        .shift-step { opacity: 0; transform: translateX(-16px); transition: all 0.5s cubic-bezier(.16,1,.3,1); }
        .shift-active .shift-step:nth-child(1) { opacity:1; transform:translateX(0); transition-delay:0s; }
        .shift-active .shift-step:nth-child(2) { opacity:1; transform:translateX(0); transition-delay:0.15s; }
        .shift-active .shift-step:nth-child(3) { opacity:1; transform:translateX(0); transition-delay:0.3s; }
        .shift-active .shift-step:nth-child(4) { opacity:1; transform:translateX(0); transition-delay:0.45s; }
      `}</style>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 }, alignItems: 'stretch' }}>
        {steps.map((s, i) => (
          <Box
            key={s.label}
            className="shift-step"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              position: 'relative',
              px: 2,
              py: 3,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: `${s.color}10`,
                border: `2px solid ${s.color}30`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: s.color }}>
                {String(i + 1).padStart(2, '0')}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: PD, mb: 0.3 }}>
              {s.label}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)' }}>
              {s.sub}
            </Typography>
            {i < 3 && (
              <Box
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  position: 'absolute',
                  top: '30px',
                  right: '-2px',
                  width: '4px',
                  height: '4px',
                  borderTop: `1.5px solid ${s.color}40`,
                  borderRight: `1.5px solid ${s.color}40`,
                  transform: 'rotate(45deg)',
                }}
              />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ─── Capabilities list ─── */
const capabilities = [
  { Icon: IconPOS, title: 'Point of Sale', desc: 'Fast, intuitive billing and order management.', bg: `${P}05`, border: `${P}12` },
  { Icon: IconKitchen, title: 'Kitchen Operations', desc: 'Real-time order flow to kitchen displays.', bg: `${A}04`, border: `${A}10` },
  { Icon: IconInventory, title: 'Inventory Intelligence', desc: 'Track materials. Smart restocking alerts.', bg: `${P}05`, border: `${P}12` },
  { Icon: IconInsights, title: 'Predictive Insights', desc: 'AI forecasting from sales, weather & events.', bg: `${A}04`, border: `${A}10` },
  { Icon: IconAccess, title: 'Role-Based Access', desc: 'Each user sees exactly what they need.', bg: `${P}05`, border: `${P}12` },
  { Icon: IconAI, title: 'AI Business Assistant', desc: 'Ask questions, get answers backed by your data.', bg: `${A}04`, border: `${A}10` },
];

/* ─── Questions Karobar answers ─── */
const questions = [
  'What should I stock more of this weekend?',
  'Which outlet is underperforming — and why?',
  'Where am I losing money without knowing?',
  'How do I increase margins without raising prices?',
  'Which staff scheduling pattern maximizes efficiency?',
];

/* ─── Flip Card component ─── */
const FlipCard = ({ Icon, title, desc, bg, border }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <Box
      onClick={() => setFlipped(!flipped)}
      sx={{
        perspective: '800px',
        cursor: 'pointer',
        aspectRatio: '1 / 1',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.5s cubic-bezier(.16,1,.3,1)',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0)',
        }}
      >
        {/* Front face */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            borderRadius: '20px',
            background: 'rgba(255,255,255,0.55)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid rgba(255,255,255,0.6)`,
            boxShadow: '0 4px 20px rgba(69,39,160,0.06), 0 1px 4px rgba(0,0,0,0.03)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 1, md: 1.5 },
            p: { xs: 1.5, md: 3 },
          }}
        >
          <Icon size={36} />
          <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.95rem' }, fontWeight: 300, color: PD, textAlign: 'center' }}>
            {title}
          </Typography>
        </Box>
        {/* Back face */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            borderRadius: '20px',
            background: `${A}08`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: `1px solid ${A}20`,
            boxShadow: `0 4px 20px ${A}10, 0 1px 4px rgba(0,0,0,0.04)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1.5, md: 3 },
          }}
        >
          <Typography sx={{ fontSize: { xs: '0.72rem', md: '0.9rem' }, color: A, fontWeight: 300, lineHeight: 1.6, textAlign: 'center' }}>
            {desc}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export const AllInOneSection = () => (
  <ColorBlock bg={BLOCKS.cream}>
    {/* ─── Header ─── */}
    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 8 } }}>
      <SectionHeading size="lg" align="center" sx={{ color: PD, mb: 2 }}>
        <Box component="span" sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic' }}>Everything</Box> you need.{' '}
        <Box
          component="span"
          sx={{ fontFamily: "'Instrument Serif', serif", fontWeight: 700, fontStyle: 'italic', background: `linear-gradient(90deg, ${P}, ${A})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
        >
          Nothing you don't.
        </Box>
      </SectionHeading>
      <BodyText align="center" sx={{ maxWidth: 600, mx: 'auto' }}>
        From orders to kitchen workflows to staff coordination — one intelligent system
        that replaces a dozen scattered tools.
      </BodyText>
    </Box>

    {/* ─── Flip capability cards grid (3×2) ─── */}
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: { xs: 1.5, md: 3 },
        mb: { xs: 6, md: 10 },
      }}
    >
      {capabilities.map(({ Icon, title, desc, bg, border }) => (
        <FlipCard key={title} Icon={Icon} title={title} desc={desc} bg={bg} border={border} />
      ))}
    </Box>

    {/* ─── Decisions block ─── */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: 'center',
        gap: { xs: 4, md: 8 },
        mb: { xs: 6, md: 10 },
        p: { xs: 3, md: 5 },
        borderRadius: '28px',
        background: 'rgba(255,255,255,0.7)',
        border: '1px solid rgba(255,255,255,0.8)',
      }}
    >
      <Box sx={{ flex: '1 1 55%', minWidth: 0, order: { xs: 2, md: 1 } }}>
        <SectionImage src={insightCards} alt="AI-powered insight cards" />
      </Box>
      <Box sx={{ flex: '1 1 45%', minWidth: 0, order: { xs: 1, md: 2 } }}>
        <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, color: PD, mb: 1.5, lineHeight: 1.2 }}>
          Built for decisions that matter.
        </Typography>
        <BodyText sx={{ mb: 3 }}>
          Your questions, answered instantly — backed by your data.
        </BodyText>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {questions.map((q, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: i % 2 === 0 ? P : A, opacity: 0.6, flexShrink: 0, mt: '8px' }} />
              <Typography sx={{ fontSize: '0.88rem', color: 'rgba(0,0,0,0.65)', lineHeight: 1.7 }}>{q}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>


  </ColorBlock>
);
